import { useTypedSelector } from "hooks";
import { BooleanSwitchValue } from "consts/general";
import { selectPaymentMethodsData } from "features/paymentMethods/selectors";
import type { PaymentMethod } from "features/paymentMethods/types";
import { useFeatureFiltersContext } from "ui/widgets/Filters";
import useMerchantDetailsTabContext from "../../useMerchantDetailsTabContext";
import type { MerchantPaymentMethodsRepository } from "../repository";
import useMerchantDetail from "../../useMerchantDetail";

export default function useAddMerchantPaymentMethod(mode: 'single' | 'bulk' = 'single') {
    const paymentMethods = useTypedSelector(selectPaymentMethodsData(({ isAssignable }) => Number(isAssignable) === 1));

    const { merchant } = useMerchantDetail();

    const repository = useMerchantDetailsTabContext<MerchantPaymentMethodsRepository>();

    const { withFilters, getFiltersState, onReset } = useFeatureFiltersContext();

    const addMerchantPaymentMethod = () => {
        const matchingMethods = withFilters<PaymentMethod>(paymentMethods);
        const selectedMethods = Array.from(new Map(
            matchingMethods.map(method => [`${method.PSP}|${method.tag}`, method])
        ).values());
        const providers = new Set(selectedMethods.map(method => method.PSP));

        if (!selectedMethods.length) {
            return Promise.reject(new Error('Select at least one payment method.'));
        }
        if (providers.size !== 1 || (mode === 'single' && selectedMethods.length !== 1)) {
            return Promise.reject(new Error(mode === 'single'
                ? 'Select exactly one Tag and one compatible PSP.'
                : 'Select one PSP and one or more methods belonging to that PSP.'));
        }

        const assignedMethodIds = new Set(repository.merchantsMethods.map(method => String(method.methodId)));
        const assignedCombinations = new Set(repository.merchantsMethods.map(method =>
            `${method.paymentMethodPSP}|${method.paymentMethodTag}`
        ));
        const methodsToCreate = selectedMethods.filter(method =>
            !assignedMethodIds.has(String(method.coreId)) &&
            !assignedCombinations.has(`${method.PSP}|${method.tag}`)
        );

        if (methodsToCreate.length !== selectedMethods.length) {
            return Promise.reject(new Error('One or more selected methods are already assigned to this merchant. Nothing was added.'));
        }

        return Promise.all(methodsToCreate.map(paymentMethod => repository.createMerchantMethodPivot({
            methodId: paymentMethod.coreId,
            merchantId: merchant.coreId,
            MOR: null,
            enabled: BooleanSwitchValue.Off
        })));
    };

    return {
        getFiltersState,
        addMerchantPaymentMethod,
        onReset
    };
};
